/**
 * Push Notifications Section — main page.
 *
 * Mirrors the Python push_notification section:
 *  - Send push notification form (title, subtitle, user filters, locales, scheduling, translation)
 *  - Notification preview card with timezone conversion
 *  - Previous push notifications table with analytics
 *  - KPI cards (Total Pushes, Shown, Clicks, Purchases, Avg CTR)
 */
import { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  ChevronDown,
  ChevronUp,
  Languages,
  Send,
  RotateCcw,
  Trash2,
  Eye,
  X,
  Clock,
  Globe2,
  Filter,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import { FilterBuilder } from '@/components/filters/FilterBuilder';
import {
  PUSH_FILTER_FIELDS,
  PUSH_FILTER_OPERATORS,
  PUSH_FILTER_FIELD_META,
} from './push.constants';
import { format as fmt } from 'date-fns';

// ─── Types ──────────────────────────────────────────────────────────────────

interface PushNotification {
  pushId: string;
  title: string;
  subtitle: string;
  selectedLanguages: string[];
  scheduleType: 'once' | 'periodic';
  scheduledTime: string;
  scheduledTimeUtc: string;
  timezone: string;
  interval?: string;
  timespan?: number;
  numTasks: number;
  numLanguages: number;
  cloudTaskIds: string[];
  filters?: FilterItem[];
  translations: Record<string, { title: string; subtitle: string }>;
  status: string;
  createdAt: string;
}

interface FilterCondition {
  type: 'filter';
  field: string;
  comparison: string;
  value: string;
  operator?: 'AND' | 'OR' | null;
}

interface FilterGroup {
  type: 'group';
  children: FilterCondition[];
  group_operator: 'AND' | 'OR';
  operator?: 'AND' | 'OR' | null;
}

type FilterItem = FilterCondition | FilterGroup;

interface AnalyticsRow {
  pushId: string;
  receives: number;
  shown: number;
  clicks: number;
  purchases: number;
  ctr: number;
}

interface Locale {
  value: string;
  label: string;
}

const SCHEDULE_INTERVALS = [
  { value: 'hourly', label: 'Every Hour' },
  { value: '6hourly', label: 'Every 6 Hours' },
  { value: '12hourly', label: 'Every 12 Hours' },
  { value: 'daily', label: 'Daily' },
  { value: '3daily', label: 'Every 3 Days' },
  { value: 'weekly', label: 'Weekly' },
];

const INTERVAL_LABELS: Record<string, string> = {
  hourly: 'Hourly',
  '6hourly': 'Every 6h',
  '12hourly': 'Every 12h',
  daily: 'Daily',
  '3daily': 'Every 3 days',
  weekly: 'Weekly',
};

const TIMESPAN_OPTIONS = [
  { value: 1, label: '1 Day' },
  { value: 3, label: '3 Days' },
  { value: 7, label: '7 Days' },
  { value: 14, label: '14 Days' },
  { value: 30, label: '30 Days' },
];

/** Operator labels for display in modals/detail views */
const FILTER_OPERATOR_LABELS: Record<string, string> = {
  '=': 'Equals', '!=': 'Not Equals', '>': 'Greater Than', '<': 'Less Than',
  '>=': 'Greater or Equal', '<=': 'Less or Equal',
  'CONTAINS': 'Contains', 'NOT_CONTAINS': 'Does Not Contain',
};

// ─── Main Page ──────────────────────────────────────────────────────────────

export function PushNotificationsPage() {
  // ── Form state ──
  const [formOpen, setFormOpen] = useState(true);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [selectedLocales, setSelectedLocales] = useState<string[]>([]);
  const [availableLocales, setAvailableLocales] = useState<Locale[]>([]);
  const [scheduleType, setScheduleType] = useState<'once' | 'periodic'>('once');
  const [startDate, setStartDate] = useState(fmt(new Date(), 'yyyy-MM-dd'));
  const [startTime, setStartTime] = useState('12:00');
  const [interval, setInterval] = useState('daily');
  const [timespan, setTimespan] = useState(7);

  // ── User filters state (base64-encoded for shared FilterBuilder) ──
  const [filtersEncoded, setFiltersEncoded] = useState('');

  // ── Translation state ──
  const [translations, setTranslations] = useState<Record<string, { title: string; subtitle: string }> | null>(null);
  const [translating, setTranslating] = useState(false);
  const [sending, setSending] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [showAllTranslations, setShowAllTranslations] = useState(false);

  // ── Data state ──
  const [pushes, setPushes] = useState<PushNotification[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  // ── Modals ──
  const [detailModal, setDetailModal] = useState<PushNotification | null>(null);
  const [filterModal, setFilterModal] = useState<FilterItem[] | null>(null);

  // ── Browser timezone ──
  const browserTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const tzName = useMemo(() => browserTimezone.split('/').pop()?.replace(/_/g, ' ') ?? browserTimezone, [browserTimezone]);

  // ── Load data on mount ──
  const loadLocales = useCallback(async () => {
    try {
      const data = await api.get<{ locales: Locale[] }>(endpoints.push.locales());
      setAvailableLocales(data.locales);
      setSelectedLocales(data.locales.map((l) => l.value));
    } catch (err) {
      console.error('Failed to load locales:', err);
    }
  }, []);

  const loadPushes = useCallback(async () => {
    setLoading(true);
    try {
      const [pushData, analyticsData] = await Promise.all([
        api.get<{ pushes: PushNotification[] }>(endpoints.push.list()),
        api.post<{ analytics: AnalyticsRow[] }>(endpoints.push.analytics(), {
          startDate: '2020-01-01',
          endDate: fmt(new Date(), 'yyyy-MM-dd'),
        }),
      ]);
      setPushes(pushData.pushes);
      setAnalytics(analyticsData.analytics);
    } catch (err) {
      console.error('Failed to load pushes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLocales();
    loadPushes();
  }, [loadLocales, loadPushes]);

  // ── Locale helpers ──

  function toggleLocale(locale: string) {
    setSelectedLocales((prev) =>
      prev.includes(locale) ? prev.filter((l) => l !== locale) : [...prev, locale],
    );
  }

  function selectAllLocales() {
    setSelectedLocales(availableLocales.map((l) => l.value));
  }

  function deselectAllLocales() {
    setSelectedLocales([]);
  }

  // ── Filter helpers ──

  /**
   * Decode the grouped FilterBuilder format into the structured format
   * the Cloud Function expects (matching Python parse_filters_to_query_params).
   *
   * Python format uses:
   *  - type: 'filter' | 'group'
   *  - comparison: '=', '!=', '>', '<', 'CONTAINS', etc.
   *  - operator: AND/OR between items (null for first item)
   *  - group_operator: AND/OR inside groups
   *  - children: nested items for groups
   */
  function decodeFiltersForApi(): FilterItem[] {
    if (!filtersEncoded) return [];
    try {
      const parsed = JSON.parse(atob(filtersEncoded));

      if (parsed && Array.isArray(parsed.groups)) {
        const topOp: 'AND' | 'OR' = parsed.groupOperator ?? 'OR';
        const groups = parsed.groups as Array<{
          id?: string;
          conditions?: Array<{ field?: string; operator?: string; value?: string }>;
          conditionOperator?: 'AND' | 'OR';
        }>;

        if (groups.length === 1) {
          const group = groups[0];
          const conditions = (group.conditions ?? []).filter(
            (c: { field?: string; value?: string }) => c.field && c.value,
          );
          if (conditions.length === 0) return [];
          return conditions.map(
            (c: { field?: string; operator?: string; value?: string }, i: number): FilterCondition => ({
              type: 'filter',
              field: c.field!,
              comparison: c.operator || '=',
              value: c.value!,
              operator: i === 0 ? null : (group.conditionOperator ?? 'AND'),
            }),
          );
        }

        const result: FilterItem[] = [];
        for (let gIdx = 0; gIdx < groups.length; gIdx++) {
          const group = groups[gIdx];
          const conditions = (group.conditions ?? []).filter(
            (c: { field?: string; value?: string }) => c.field && c.value,
          );
          if (conditions.length === 0) continue;

          const children: FilterCondition[] = conditions.map(
            (c: { field?: string; operator?: string; value?: string }, i: number): FilterCondition => ({
              type: 'filter',
              field: c.field!,
              comparison: c.operator || '=',
              value: c.value!,
              operator: i === 0 ? null : (group.conditionOperator ?? 'AND'),
            }),
          );

          if (children.length === 1) {
            result.push({
              ...children[0],
              operator: result.length === 0 ? null : topOp,
            });
          } else {
            result.push({
              type: 'group',
              children,
              group_operator: group.conditionOperator ?? 'AND',
              operator: result.length === 0 ? null : topOp,
            });
          }
        }
        return result;
      }

      if (Array.isArray(parsed)) {
        return parsed
          .filter((f: { field?: string; value?: string }) => f.field && f.value)
          .map((f: { type?: string; field: string; comparison?: string; operator?: string; value: string }, i: number): FilterCondition => ({
            type: 'filter',
            field: f.field,
            comparison: f.comparison || f.operator || '=',
            value: f.value,
            operator: i === 0 ? null : ('AND' as const),
          }));
      }

      return [];
    } catch {
      return [];
    }
  }

  // ── UTC conversion helpers ──

  const localDt = useMemo(() => `${startDate}T${startTime}:00`, [startDate, startTime]);
  const utcDt = useMemo(() => {
    try {
      return new Date(localDt).toISOString();
    } catch {
      return '';
    }
  }, [localDt]);
  const utcDisplay = useMemo(() => {
    try {
      const d = new Date(localDt);
      return fmt(d, "yyyy-MM-dd HH:mm") + ' UTC';
    } catch {
      return '';
    }
  }, [localDt]);

  // ── Number of tasks preview ──
  const previewNumTasks = useMemo(() => {
    const numLocales = selectedLocales.length;
    if (numLocales === 0) return 0;
    if (scheduleType === 'once') return numLocales;
    const intervalMs = getIntervalMs(interval);
    const timespanMs = timespan * 24 * 60 * 60 * 1000;
    const numSchedules = Math.max(1, Math.floor(timespanMs / intervalMs));
    return numLocales * numSchedules;
  }, [selectedLocales, scheduleType, interval, timespan]);

  // ── Is scheduled time in the future? ──
  const isTimeInFuture = useMemo(() => {
    try {
      return new Date(localDt) > new Date();
    } catch {
      return false;
    }
  }, [localDt]);

  // ── Translate ──

  async function handleTranslate() {
    if (!title.trim() || !subtitle.trim()) {
      setStatusMsg({ type: 'error', text: 'Please enter both title and subtitle.' });
      return;
    }
    if (selectedLocales.length === 0) {
      setStatusMsg({ type: 'error', text: 'Please select at least one locale.' });
      return;
    }

    const baseLangs = new Set(selectedLocales.map((l) => l.split('_')[0].toLowerCase()).filter((b) => b !== 'en'));
    const numToTranslate = baseLangs.size;

    setTranslating(true);
    setStatusMsg({
      type: 'warning',
      text: `Translating to ${numToTranslate} language${numToTranslate !== 1 ? 's' : ''}... This may take ${numToTranslate * 2}-${numToTranslate * 3} seconds.`,
    });
    setShowAllTranslations(false);

    try {
      const data = await api.post<{
        translations: Record<string, { title: string; subtitle: string }>;
        failedLanguages?: string[];
      }>(
        endpoints.push.translate(),
        { title, subtitle, selectedLanguages: selectedLocales },
      );
      setTranslations(data.translations);

      const failed = data.failedLanguages ?? [];
      if (failed.length > 0) {
        setStatusMsg({
          type: 'warning',
          text: `Translations partially complete — ${failed.length} locale(s) fell back to English: ${failed.join(', ')}`,
        });
      } else {
        setStatusMsg({
          type: 'success',
          text: `Translations complete (${Object.keys(data.translations).length} locales). Click any translation to edit.`,
        });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Translation failed';
      setStatusMsg({ type: 'error', text: msg });
    } finally {
      setTranslating(false);
    }
  }

  // ── Send ──

  async function handleSend() {
    if (!translations) {
      setStatusMsg({ type: 'error', text: 'Please translate first.' });
      return;
    }
    if (!isTimeInFuture) {
      setStatusMsg({ type: 'error', text: 'Scheduled time must be in the future.' });
      return;
    }

    setSending(true);
    setStatusMsg(null);
    try {
      const validFilters = decodeFiltersForApi();

      const data = await api.post<{ pushId: string; numTasks: number }>(
        endpoints.push.send(),
        {
          title,
          subtitle,
          selectedLanguages: selectedLocales,
          scheduleType,
          scheduledTime: localDt,
          scheduledTimeUtc: utcDt,
          timezone: browserTimezone,
          ...(scheduleType === 'periodic' ? { interval, timespan } : {}),
          translations,
          ...(validFilters.length > 0 ? { filters: validFilters } : {}),
        },
      );

      setStatusMsg({
        type: 'success',
        text: `Push notification sent! Created ${data.numTasks} Cloud Task(s). Push ID: ${data.pushId.slice(0, 16)}...`,
      });
      setTitle('');
      setSubtitle('');
      setTranslations(null);
      setFiltersEncoded('');
      loadPushes();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Send failed';
      setStatusMsg({ type: 'error', text: msg });
    } finally {
      setSending(false);
    }
  }

  // ── Delete ──

  async function handleDelete(pushId: string) {
    setDeleting(pushId);
    try {
      await api.delete(endpoints.push.delete(pushId));
      loadPushes();
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(null);
    }
  }

  async function handleDeleteAll() {
    if (!confirm('Delete ALL push notifications and their Cloud Tasks?')) return;
    setDeleting('all');
    try {
      await api.delete(endpoints.push.deleteAll());
      loadPushes();
    } catch (err) {
      console.error('Delete all failed:', err);
    } finally {
      setDeleting(null);
    }
  }

  // ── Computed KPIs ──
  const kpis = useMemo(() => {
    const total = pushes.length;
    let shown = 0, clicks = 0, purchases = 0;
    analytics.forEach((a) => { shown += a.shown; clicks += a.clicks; purchases += a.purchases; });
    const avgCtr = shown > 0 ? clicks / shown : 0;
    return { total, shown, clicks, purchases, avgCtr };
  }, [pushes, analytics]);

  // ── Push-to-Analytics map ──
  const analyticsMap = useMemo(() => {
    const m: Record<string, AnalyticsRow> = {};
    analytics.forEach((a) => { m[a.pushId] = a; });
    return m;
  }, [analytics]);

  // ── Translation entries, split for "show more" ──
  const translationEntries = useMemo(() => (translations ? Object.entries(translations) : []), [translations]);
  const visibleTranslations = showAllTranslations ? translationEntries : translationEntries.slice(0, 5);
  const hiddenCount = translationEntries.length - 5;

  // ── All locales selected? ──
  const allLocalesSelected = selectedLocales.length === availableLocales.length && availableLocales.length > 0;

  return (
    <div className="px-8 py-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#8B5CF612' }}>
          <Bell className="w-5 h-5" style={{ color: '#8B5CF6' }} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Push Notifications</h1>
          <p className="text-xs text-slate-500">Send, translate, schedule, and analyze push notifications</p>
        </div>
      </div>

      {/* ── Send Push Form ── */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-soft-xs mb-6 overflow-hidden">
        <button
          onClick={() => setFormOpen((p) => !p)}
          className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50/50 transition-colors"
        >
          <span className="text-sm font-semibold text-slate-900">Send Push Notification</span>
          {formOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>

        <AnimatePresence initial={false}>
          {formOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-5 pb-5 space-y-5 border-t border-slate-100">
                {/* Title */}
                <div style={{ marginTop: 12 }}>
                  <label className="text-xs font-semibold text-slate-700 mb-1.5 block">
                    Notification Title (English)
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter notification title..."
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                  />
                </div>

                {/* Subtitle */}
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1.5 block">
                    Notification Subtitle (English)
                  </label>
                  <textarea
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    placeholder="Enter notification subtitle/body..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 resize-y"
                  />
                </div>

                {/* ── User Filters (shared FilterBuilder with grouping) ── */}
                <div className="border border-slate-200 rounded-lg px-3 py-2.5">
                  <FilterBuilder
                    value={filtersEncoded}
                    onChange={setFiltersEncoded}
                    fields={PUSH_FILTER_FIELDS}
                    operators={PUSH_FILTER_OPERATORS}
                    fieldMeta={PUSH_FILTER_FIELD_META}
                  />
                </div>

                {/* Target Locales */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                      <Globe2 className="w-3.5 h-3.5" /> Target Locales
                    </label>
                    <div className="flex gap-2">
                      <button onClick={selectAllLocales} className="text-2xs text-violet-600 hover:text-violet-700 font-medium">
                        Select All
                      </button>
                      <button onClick={deselectAllLocales} className="text-2xs text-slate-400 hover:text-slate-600 font-medium">
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 p-3 border border-slate-200 rounded-lg bg-slate-50/50 max-h-40 overflow-y-auto">
                    {availableLocales.map((locale) => (
                      <button
                        key={locale.value}
                        onClick={() => toggleLocale(locale.value)}
                        className={cn(
                          'px-2 py-0.5 text-2xs rounded-md border transition-colors whitespace-nowrap',
                          selectedLocales.includes(locale.value)
                            ? 'bg-violet-100 border-violet-300 text-violet-700'
                            : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300',
                        )}
                      >
                        {locale.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-2xs text-slate-400 mt-1">
                    {allLocalesSelected
                      ? `All ${availableLocales.length} locales selected`
                      : `${selectedLocales.length} locale(s) selected`}
                  </p>
                </div>

                {/* Scheduling */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" /> Scheduling Type
                    </label>
                    <select
                      value={scheduleType}
                      onChange={(e) => setScheduleType(e.target.value as 'once' | 'periodic')}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                    >
                      <option value="once">Send Once at Specific Date/Time</option>
                      <option value="periodic">Send Periodically with Interval</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Start Date</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        min={fmt(new Date(), 'yyyy-MM-dd')}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Start Time</label>
                      <input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                      />
                    </div>
                  </div>
                </div>

                {/* Periodic Options */}
                {scheduleType === 'periodic' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 border border-dashed border-slate-200 rounded-lg bg-slate-50/30">
                    <div>
                      <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Interval</label>
                      <select
                        value={interval}
                        onChange={(e) => setInterval(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                      >
                        {SCHEDULE_INTERVALS.map((i) => (
                          <option key={i.value} value={i.value}>{i.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Time Span</label>
                      <select
                        value={timespan}
                        onChange={(e) => setTimespan(Number(e.target.value))}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                      >
                        {TIMESPAN_OPTIONS.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* ── Preview Section ── */}
                <div className="p-4 bg-slate-50/80 rounded-lg border border-slate-100 space-y-3">
                  <h4 className="text-xs font-semibold text-slate-600">Preview</h4>

                  {/* Notification preview card */}
                  {title.trim() && subtitle.trim() && (
                    <div className="p-3 bg-white border border-slate-200 rounded-lg">
                      <div className="text-sm font-semibold text-slate-800">{title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
                    </div>
                  )}

                  <div className="text-2xs text-slate-500 space-y-1">
                    <p>
                      <span className="font-medium text-slate-700">Your Time:</span>{' '}
                      {startDate} {startTime}{' '}
                      <span className="text-slate-400">({tzName})</span>
                    </p>
                    <p>
                      <span className="font-medium text-slate-700">UTC Time:</span>{' '}
                      <span className="text-slate-400">{utcDisplay}</span>
                    </p>
                    <p>
                      <span className="font-medium text-slate-700">Type:</span>{' '}
                      {scheduleType === 'once'
                        ? 'One-time'
                        : `Periodic (${SCHEDULE_INTERVALS.find((i) => i.value === interval)?.label})`}
                    </p>
                    <p>
                      <span className="font-medium text-slate-700">Locales:</span>{' '}
                      {allLocalesSelected
                        ? `All ${availableLocales.length} locales`
                        : `${selectedLocales.length} locale${selectedLocales.length !== 1 ? 's' : ''} selected`}
                    </p>
                    <div className="pt-1 border-t border-slate-100 mt-1">
                      <span className="font-medium text-slate-700">Total Cloud Tasks:</span>{' '}
                      <span className="inline-flex items-center justify-center px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded text-2xs font-semibold">
                        {previewNumTasks}
                      </span>
                      <span className="text-slate-400 ml-1">(one task per locale per scheduled time)</span>
                    </div>
                  </div>
                </div>

                {/* Translate Button */}
                <button
                  onClick={handleTranslate}
                  disabled={translating || !title.trim() || !subtitle.trim() || selectedLocales.length === 0}
                  className={cn(
                    'w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    translating
                      ? 'bg-violet-200 text-violet-500 cursor-wait'
                      : 'bg-violet-600 text-white hover:bg-violet-700 active:bg-violet-800',
                    (!title.trim() || !subtitle.trim() || selectedLocales.length === 0) && !translating && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />}
                  {translating ? 'Translating...' : 'Translate Notifications'}
                </button>

                {/* Translation Review */}
                {translations && (
                  <div className="border border-emerald-200 rounded-lg p-4 bg-emerald-50/30 space-y-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs font-semibold text-emerald-700">
                        Translations Complete ({translationEntries.length} locales)
                      </span>
                      <span className="text-2xs text-slate-400">Click any translation to edit</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {visibleTranslations.map(([locale, t]) => (
                        <div key={locale} className="p-2.5 bg-white border border-slate-200 rounded-lg">
                          <div className="text-2xs font-semibold text-violet-600 mb-1">{locale.toUpperCase()}</div>
                          <input
                            value={t.title}
                            onChange={(e) =>
                              setTranslations((prev) =>
                                prev ? { ...prev, [locale]: { ...prev[locale], title: e.target.value } } : null,
                              )
                            }
                            className="w-full px-2 py-1 text-xs border border-slate-100 rounded mb-1 focus:outline-none focus:ring-1 focus:ring-violet-400"
                          />
                          <textarea
                            value={t.subtitle}
                            onChange={(e) =>
                              setTranslations((prev) =>
                                prev ? { ...prev, [locale]: { ...prev[locale], subtitle: e.target.value } } : null,
                              )
                            }
                            rows={2}
                            className="w-full px-2 py-1 text-xs border border-slate-100 rounded focus:outline-none focus:ring-1 focus:ring-violet-400 resize-none"
                          />
                        </div>
                      ))}
                    </div>

                    {hiddenCount > 0 && (
                      <button
                        onClick={() => setShowAllTranslations((p) => !p)}
                        className="text-2xs font-medium text-violet-600 hover:text-violet-700 transition-colors"
                      >
                        {showAllTranslations ? 'Show Less' : `Show All ${translationEntries.length} Translations`}
                      </button>
                    )}
                  </div>
                )}

                {/* Send / Retranslate Buttons */}
                {translations && (
                  <div className="flex gap-3">
                    <button
                      onClick={handleSend}
                      disabled={sending || !isTimeInFuture}
                      className={cn(
                        'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
                        sending
                          ? 'bg-emerald-200 text-emerald-500 cursor-wait'
                          : !isTimeInFuture
                            ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                            : 'bg-emerald-600 text-white hover:bg-emerald-700',
                      )}
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      {sending ? 'Sending...' : !isTimeInFuture ? 'Scheduled time must be in the future' : 'Confirm & Send'}
                    </button>
                    <button
                      onClick={handleTranslate}
                      disabled={translating}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Retranslate
                    </button>
                  </div>
                )}

                {/* Status Message */}
                {statusMsg && (
                  <div
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium',
                      statusMsg.type === 'success' && 'bg-emerald-50 text-emerald-700',
                      statusMsg.type === 'error' && 'bg-red-50 text-red-700',
                      statusMsg.type === 'warning' && 'bg-amber-50 text-amber-700',
                    )}
                  >
                    {statusMsg.type === 'success' && <CheckCircle2 className="w-4 h-4 shrink-0" />}
                    {statusMsg.type === 'error' && <AlertCircle className="w-4 h-4 shrink-0" />}
                    {statusMsg.type === 'warning' && <Loader2 className="w-4 h-4 animate-spin shrink-0" />}
                    {statusMsg.text}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <KpiCard label="Total Pushes" value={commaFmt(kpis.total)} color="#8B5CF6" />
        <KpiCard label="Total Shown" value={commaFmt(kpis.shown)} color="#8B5CF6" />
        <KpiCard label="Total Clicks" value={commaFmt(kpis.clicks)} color="#8B5CF6" />
        <KpiCard label="Total Purchases" value={commaFmt(kpis.purchases)} color="#8B5CF6" />
        <KpiCard label="Avg CTR" value={`${(kpis.avgCtr * 100).toFixed(2)}%`} color="#8B5CF6" />
      </div>

      {/* ── Previous Push Notifications ── */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-soft-xs overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">Previous Push Notifications</h3>
          <button
            onClick={handleDeleteAll}
            disabled={deleting !== null || pushes.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-2xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
          >
            <Trash2 className="w-3 h-3" />
            Delete All
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-violet-400" />
          </div>
        ) : pushes.length === 0 ? (
          <div className="text-center py-12 text-xs text-slate-400">
            No push notifications found. Create your first one above!
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-100">
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Push ID</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Title / Subtitle</th>
                  <th className="text-center px-4 py-2.5 font-semibold text-slate-600">Locales</th>
                  <th className="text-center px-4 py-2.5 font-semibold text-slate-600">Period</th>
                  <th className="text-center px-4 py-2.5 font-semibold text-slate-600">Scheduled</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Receives</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Shown</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Clicks</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Purchases</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-600">CTR</th>
                  <th className="text-center px-4 py-2.5 font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pushes.map((push) => {
                  const a = analyticsMap[push.pushId];
                  const receives = a?.receives ?? 0;
                  const shown = a?.shown ?? 0;
                  const clicks = a?.clicks ?? 0;
                  const purchases = a?.purchases ?? 0;
                  const ctr = shown > 0 ? (clicks / shown * 100) : 0;

                  // Period label
                  const periodLabel = push.scheduleType === 'once'
                    ? 'One-time'
                    : `${INTERVAL_LABELS[push.interval ?? 'daily'] ?? push.interval} × ${push.timespan}d`;

                  // Scheduled time display
                  const schedStr = formatDate(push.scheduledTime);
                  const schedTz = push.timezone?.split('/').pop()?.replace(/_/g, ' ') ?? '';

                  // Locale badge
                  const numLangs = push.numLanguages || push.selectedLanguages?.length || 0;
                  const isAllLocales = numLangs >= 24;

                  return (
                    <tr key={push.pushId} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-4 py-2.5">
                        <code className="text-2xs text-slate-400">{push.pushId.slice(0, 12)}...</code>
                      </td>
                      <td className="px-4 py-2.5 max-w-[200px]">
                        <button
                          onClick={() => setDetailModal(push)}
                          className="text-left hover:text-violet-600 transition-colors"
                          title={`${push.title}\n${push.subtitle}`}
                        >
                          <div className="font-medium text-slate-800 truncate max-w-[200px]">{push.title}</div>
                          <div className="text-slate-400 truncate max-w-[200px]">{push.subtitle}</div>
                        </button>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={cn(
                          'inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-medium',
                          isAllLocales ? 'bg-slate-100 text-slate-500' : 'bg-violet-100 text-violet-700',
                        )}>
                          {isAllLocales ? `All ${numLangs}` : numLangs === 1 ? push.selectedLanguages?.[0] : `${numLangs} locales`}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center text-slate-500 text-2xs">{periodLabel}</td>
                      <td className="px-4 py-2.5 text-center text-slate-500 text-2xs whitespace-nowrap">
                        {schedStr}
                        {schedTz && <div className="text-slate-400">({schedTz})</div>}
                      </td>
                      <td className="px-4 py-2.5 text-right text-slate-700 tabular-nums">{receives > 0 ? commaFmt(receives) : '-'}</td>
                      <td className="px-4 py-2.5 text-right text-slate-700 tabular-nums">{shown > 0 ? commaFmt(shown) : '-'}</td>
                      <td className="px-4 py-2.5 text-right text-slate-700 tabular-nums">{clicks > 0 ? commaFmt(clicks) : '-'}</td>
                      <td className="px-4 py-2.5 text-right text-slate-700 tabular-nums">{purchases > 0 ? commaFmt(purchases) : '-'}</td>
                      <td className="px-4 py-2.5 text-right text-slate-700 tabular-nums">{shown > 0 ? `${ctr.toFixed(2)}%` : '-'}</td>
                      <td className="px-4 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setDetailModal(push)}
                            className="p-1 text-slate-400 hover:text-violet-600 transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          {push.filters && push.filters.length > 0 && (
                            <button
                              onClick={() => setFilterModal(push.filters ?? null)}
                              className="p-1 text-slate-400 hover:text-amber-600 transition-colors"
                              title="View Filters"
                            >
                              <Filter className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(push.pushId)}
                            disabled={deleting === push.pushId}
                            className="p-1 text-slate-400 hover:text-red-600 transition-colors disabled:opacity-50"
                            title="Delete"
                          >
                            {deleting === push.pushId ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Detail Modal ── */}
      {detailModal && (
        <Modal title={`Push Details: ${detailModal.title.slice(0, 50)}...`} onClose={() => setDetailModal(null)}>
          <div className="space-y-4">
            {/* Title & subtitle */}
            <div>
              <h4 className="text-sm font-semibold text-slate-800 mb-1">{detailModal.title}</h4>
              <p className="text-xs text-slate-500">{detailModal.subtitle}</p>
            </div>
            <hr className="border-slate-100" />

            {/* Info rows */}
            <div className="space-y-2 text-xs">
              <InfoRow label="Push ID" value={detailModal.pushId} mono />
              <InfoRow label="Scheduled" value={`${formatDate(detailModal.scheduledTime)}${detailModal.timezone ? ` (${detailModal.timezone})` : ''}`} />
              <InfoRow label="UTC Time" value={detailModal.scheduledTimeUtc ? formatDate(detailModal.scheduledTimeUtc) + ' UTC' : '-'} />
              <InfoRow label="Type" value={detailModal.scheduleType === 'once' ? 'One-time' : `Periodic (${INTERVAL_LABELS[detailModal.interval ?? 'daily'] ?? detailModal.interval})`} />
              <InfoRow label="Status" value={detailModal.status} />
              <InfoRow label="Tasks" value={commaFmt(detailModal.numTasks)} />
              <InfoRow label="Languages" value={detailModal.selectedLanguages?.join(', ') ?? '-'} />
              <InfoRow label="Created" value={formatDate(detailModal.createdAt)} />
            </div>

            {/* Filters */}
            {detailModal.filters && detailModal.filters.length > 0 && (
              <>
                <hr className="border-slate-100" />
                <div>
                  <h5 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                    <Filter className="w-3.5 h-3.5" /> User Filters
                  </h5>
                  <div className="space-y-1.5">
                    {detailModal.filters.map((f, i) => (
                      <FilterItemDisplay key={i} item={f} index={i} />
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Translations */}
            {detailModal.translations && Object.keys(detailModal.translations).length > 0 && (
              <>
                <hr className="border-slate-100" />
                <div>
                  <h5 className="text-xs font-semibold text-slate-700 mb-2">Translations</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {Object.entries(detailModal.translations).map(([loc, t]) => (
                      <div key={loc} className="p-2 border border-slate-100 rounded-lg">
                        <div className="text-2xs font-semibold text-violet-600">{loc.toUpperCase()}</div>
                        <div className="text-2xs text-slate-700">{t.title}</div>
                        <div className="text-2xs text-slate-400">{t.subtitle}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* No filters message */}
            {(!detailModal.filters || detailModal.filters.length === 0) && (
              <>
                <hr className="border-slate-100" />
                <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-50 px-3 py-2 rounded-lg">
                  <Filter className="w-3.5 h-3.5" />
                  No user filters applied — sent to all users.
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* ── Filters Modal ── */}
      {filterModal && (
        <Modal title="Push Notification Filters" onClose={() => setFilterModal(null)}>
          <div className="space-y-2">
            <p className="text-2xs text-slate-400 mb-3">Total filter conditions: {filterModal.length}</p>
            {filterModal.map((f, i) => (
              <FilterItemDisplay key={i} item={f} index={i} />
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function FilterItemDisplay({ item, index }: { item: FilterItem; index: number }) {
  if (item.type === 'group') {
    return (
      <div>
        {index > 0 && item.operator && (
          <div className="my-1.5">
            <span className={cn('text-2xs font-medium px-2 py-1 rounded', item.operator === 'OR' ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600')}>
              {item.operator}
            </span>
          </div>
        )}
        <div className="p-3 bg-blue-50/50 rounded-lg border-2 border-blue-200 space-y-1.5">
          <div className="text-2xs font-semibold text-blue-600 mb-2">
            Group ({item.group_operator})
          </div>
          {item.children.map((child, ci) => (
            <FilterItemDisplay key={ci} item={child} index={ci} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {index > 0 && item.operator && (
        <div className="my-1.5">
          <span className={cn('text-2xs font-medium px-2 py-1 rounded', item.operator === 'OR' ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600')}>
            {item.operator}
          </span>
        </div>
      )}
      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-xs space-y-1">
        <div><span className="font-medium text-slate-500">Field: </span><span className="text-violet-600">{item.field}</span></div>
        <div><span className="font-medium text-slate-500">Operator: </span>{getOperatorLabel(item.comparison)}</div>
        <div><span className="font-medium text-slate-500">Value: </span><code className="text-2xs">{item.value}</code></div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200/80 shadow-soft-xs px-4 py-3">
      <div className="text-2xs text-slate-500 mb-0.5">{label}</div>
      <div className="text-lg font-bold tabular-nums" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-lg border border-slate-200 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 sticky top-0 bg-white rounded-t-xl z-10">
          <h3 className="text-sm font-semibold text-slate-900 truncate pr-4">{title}</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 transition-colors shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-3">
      <span className="text-slate-500 font-medium min-w-[90px] shrink-0">{label}:</span>
      <span className={cn('text-slate-700 break-all', mono && 'font-mono text-2xs')}>{value}</span>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function getOperatorLabel(op: string): string {
  return FILTER_OPERATOR_LABELS[op] ?? op;
}

function commaFmt(n: number): string {
  return n.toLocaleString('en-US');
}

function formatDate(iso: string): string {
  try {
    return fmt(new Date(iso), 'yyyy-MM-dd HH:mm');
  } catch {
    return iso;
  }
}

function getIntervalMs(interval: string): number {
  const HOUR = 60 * 60 * 1000;
  switch (interval) {
    case 'hourly': return HOUR;
    case '6hourly': return 6 * HOUR;
    case '12hourly': return 12 * HOUR;
    case 'daily': return 24 * HOUR;
    case '3daily': return 3 * 24 * HOUR;
    case 'weekly': return 7 * 24 * HOUR;
    default: return 24 * HOUR;
  }
}
